import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChatPanel } from "@/features/chat/components/ChatPanel";
import { MapPanel } from "@/features/map/components/MapPanel";
import { MapOverlay } from "@/features/map/components/MapOverlay";
import { Sidebar } from "@/app/Sidebar";
import { FocusTrap } from "@/components/a11y/FocusTrap";
import { useMapStore } from "@/stores/mapStore";

/* ── Icons ── */

function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
  );
}

/* ── MobileLayout ── */

interface MobileLayoutProps {
  onSendMessage: (content: string) => void;
  onEntityClick: (entityType: string, entityId: string, name: string) => void;
  onStopStreaming?: () => void;
  onOpenSettings?: () => void;
  onLoadConversation: (conversationId: string) => void;
  onDeleteConversation: (conversationId: string) => void;
  onAskAbout: (entityType: string, entityId: string, name: string) => void;
  disabled: boolean;
}

export function MobileLayout({
  onSendMessage,
  onEntityClick,
  onStopStreaming,
  onOpenSettings,
  onLoadConversation,
  onDeleteConversation,
  onAskAbout,
  disabled,
}: MobileLayoutProps) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mapOverlayOpen = useMapStore((s) => s.mapOverlayOpen);
  const setMapOverlayOpen = useMapStore((s) => s.setMapOverlayOpen);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Close drawer on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setDrawerOpen(false);
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [drawerOpen]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const handleLoadConversation = useCallback(
    async (id: string) => {
      closeDrawer();
      await onLoadConversation(id);
    },
    [onLoadConversation, closeDrawer],
  );

  const handleAskAboutFromOverlay = useCallback(
    (entityType: string, entityId: string, name: string) => {
      setMapOverlayOpen(false);
      onAskAbout(entityType, entityId, name);
    },
    [onAskAbout, setMapOverlayOpen],
  );

  return (
    <div className="cx-mobile-full-height flex flex-col">
      {/* Header */}
      <div className="cx-mobile-header">
        <button
          className="cx-mobile-header-btn"
          onClick={openDrawer}
          aria-label={t("nav.menu")}
        >
          <MenuIcon />
        </button>
        <span className="cx-brand-text flex-1 pl-1">
          Ortvis
        </span>
        <button
          className="cx-mobile-header-btn"
          onClick={() => setMapOverlayOpen(true)}
          aria-label={t("nav.showMap")}
        >
          <MapIcon />
        </button>
      </div>

      {/* Chat (always visible) */}
      <div className="flex-1 min-h-0 cx-safe-bottom">
        <ChatPanel
          onSendMessage={onSendMessage}
          onEntityClick={onEntityClick}
          onStopStreaming={onStopStreaming}
          disabled={disabled}
        />
      </div>

      {/* Map overlay (full-screen) */}
      <MapOverlay open={mapOverlayOpen} onClose={() => setMapOverlayOpen(false)}>
        <MapPanel onAskAbout={handleAskAboutFromOverlay} />
      </MapOverlay>

      {/* Drawer overlay */}
      {drawerOpen && (
        <div
          className="cx-mobile-drawer-backdrop animate-cx-fade-in"
          onClick={closeDrawer}
        />
      )}

      {/* Drawer */}
      <div className={`cx-mobile-drawer${drawerOpen ? " cx-mobile-drawer--open" : ""}`}>
        <FocusTrap active={drawerOpen}>
          <Sidebar
            isCollapsed={false}
            onToggle={closeDrawer}
            onLoadConversation={handleLoadConversation}
            onDeleteConversation={onDeleteConversation}
            onOpenSettings={onOpenSettings}
          />
        </FocusTrap>
      </div>
    </div>
  );
}
